// The Fleet Live body (#6777, epic #6552): the org's live pipeline, from stored rows only.
// `GET /v1/fleet/live` serves it whole; the `fleet_live` topic of `GET /v1/streams` serves
// the same body as patch frames. One builder produces both (`buildFleetLive`,
// `apps/api/src/routes/fleet-live.ts`); its docblock carries the sources table, naming
// each field's stored source. The UI is #6778 (Command Deck, Topology) and #6779 (Core
// Intelligence).
//
// OMITTED, NEVER SIMULATED: a number with no stored source is ABSENT from the body. It is
// never `0` and never `null` passed off as a reading. The optional fields below are the
// ones that can be absent; each says when. A count over rows that exist (`counts.*`,
// `merged_today.count`) is a reading, so its `0` is real.
//
// TENANT: every field is the account's own. A machine from the signed-in person's other
// orgs (owner ruling v1 G23) is never in it.

import { z } from 'zod';
import { FLEET_LIVE_STAGES, FLEET_LIVE_STATES } from '../value-objects/fleet-live-stage';

const iso = z.string();
const count = z.number().int().nonnegative();

/** An open blocker as Fleet Live names it: `blockers.id` (`blk_…`) and `blockers.summary`. */
export const FleetLiveBlockerWireSchema = z.object({ id: z.string(), reason: z.string() });

/** One deck item: a coding task, or an ask (raw task) that has not yet become one. */
export const FleetLiveItemWireSchema = z.object({
  /** `tasks.id` (`tsk_…`) or `raw_tasks.id` (`rtk_…`). */
  id: z.string(),
  /** The PR number, else the issue number, when one exists. */
  number: z.number().int().positive().optional(),
  /** The task's own stored title (`envelope.title`); for an ask, its issue title or first line. */
  title: z.string(),
  stage: z.enum(FLEET_LIVE_STAGES),
  state: z.enum(FLEET_LIVE_STATES),
  /** The machine holding the task, when one of THIS account's machines does. */
  runner_id: z.string().optional(),
  /** That machine's name plus its 8-character id, never its hostname. */
  machine_label: z.string().optional(),
  /** The newest open blocker on the task. */
  blocker: FleetLiveBlockerWireSchema.optional(),
  /** CI fix rounds on the item's PR; absent when the item has no PR. */
  ci_fix_rounds: count.optional(),
  /** The item's `created_at`. */
  since: iso,
  /** The effective squad of the item's repo; absent when it names no enabled repo of the account. */
  squad_key: z.string().optional(),
});

/** One machine of the `GET /v1/fleet/utilization` rows, as the deck draws it. */
export const FleetLiveMachineWireSchema = z.object({
  runner_id: z.string(),
  label: z.string(),
  /** The row's `machineVerdict` status, only the online ones. */
  status: z.enum(['working', 'idle', 'paused', 'starting']),
  /** Load over cores, as a percentage; absent when the machine reports no CPU reading. */
  cpu_pct: count.optional(),
  /** Live sessions on the machine's presence beat. */
  jobs: count,
});

/** A count since 00:00 UTC today; `since` is that instant. */
const SinceTodaySchema = z.object({ count, since: iso });

export const FleetLiveSquadWireSchema = z.object({
  /** `sq_<id>` for a named squad, `repo_<repoId>` for a repo standing as its own. */
  key: z.string(),
  name: z.string(),
  implicit: z.boolean(),
  repos: z.array(z.object({ id: z.string(), full_name: z.string() })),
});

export const FleetLiveAgentBucketsWireSchema = z.object({
  writing: count,
  reviewing: count,
  fixing: count,
});

export const FLEET_LIVE_EVENT_KINDS = [
  'ask',
  'pick',
  'pr',
  'ci',
  'blocked',
  'parked',
  'resolved',
  'merged',
  'shipped',
  'aside',
] as const;
export type FleetLiveEventKind = (typeof FLEET_LIVE_EVENT_KINDS)[number];

/** One transition. It carries its own labels: its item may be outside the 100 items shown. */
export const FleetLiveEventWireSchema = z.object({
  /** The stored timestamp of the transition. */
  at: iso,
  kind: z.enum(FLEET_LIVE_EVENT_KINDS),
  item: z.object({
    id: z.string(),
    number: z.number().int().positive().optional(),
    title: z.string(),
  }),
  machine_label: z.string().optional(),
  blocker: FleetLiveBlockerWireSchema.optional(),
});

export const FleetLiveWireSchema = z.object({
  /** At most 100, newest activity first. */
  items: z.array(FleetLiveItemWireSchema),
  counts: z.object({ in_flight: count, queued: count, in_ci: count }),
  machines: z.object({
    /** At most 16, busiest first. */
    shown: z.array(FleetLiveMachineWireSchema),
    /** Online machines. Absent with `busy` when the presence store could not be read. */
    online_total: count.optional(),
    /** Online machines holding at least one job. */
    busy: count.optional(),
  }),
  merged_today: SinceTodaySchema,
  shipped_today: SinceTodaySchema,
  /** Median minutes from task creation to merge over the last 24 h; absent when none merged. */
  median_cycle_minutes: z.number().nonnegative().optional(),
  /** Open PRs whose task is `mergeable`; `by_squad` over the squads shown. */
  at_gate: z.object({ total: count, by_squad: z.record(z.string(), count) }),
  /** Per shown squad, its newest merge; a squad with no merged PR has no key. */
  last_merge_by_squad: z.record(z.string(), z.object({ number: z.number().int(), at: iso })),
  /** Commits per minute over the last 15 complete minutes; absent when there is no row. */
  commits: z
    .object({
      per_minute: z.number().nonnegative(),
      window_minutes: z.literal(15),
      since: iso,
      /** Per shown squad: commits per minute over the same window. */
      by_squad: z.record(z.string(), z.number().nonnegative()),
    })
    .optional(),
  agents: z.object({
    /** Every live agent, so it can exceed the buckets' sum (bucket `none`, a session with no repo). */
    total: count,
    by_squad: z.record(z.string(), FleetLiveAgentBucketsWireSchema),
  }),
  /** At most 8, ranked (#7010 D5). */
  squads: z.array(FleetLiveSquadWireSchema),
  /** Squads past the 8 shown. */
  squads_more: count,
  /** The 14 newest transitions, newest first. */
  events: z.array(FleetLiveEventWireSchema),
});

export type FleetLiveWire = z.infer<typeof FleetLiveWireSchema>;
export type FleetLiveItemWire = z.infer<typeof FleetLiveItemWireSchema>;
export type FleetLiveMachineWire = z.infer<typeof FleetLiveMachineWireSchema>;
export type FleetLiveEventWire = z.infer<typeof FleetLiveEventWireSchema>;
export type FleetLiveSquadWire = z.infer<typeof FleetLiveSquadWireSchema>;
